import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import type { LandingPageAnalysis } from '@/types/landing-page-analysis';

// ============================================
// Comparison Score Card — Landing Page Scores
// ============================================

interface ComparisonScoreCardProps {
  landingPage?: LandingPageAnalysis;
  showIssues?: boolean;
}

function getColor(score: number): { bar: string; text: string } {
  if (score >= 80) return { bar: 'bg-[var(--ed-green)]', text: 'text-[var(--ed-green)]' };
  if (score >= 60) return { bar: 'bg-[var(--ed-amber)]', text: 'text-[var(--ed-amber)]' };
  return { bar: 'bg-[var(--ed-red)]', text: 'text-[var(--ed-red)]' };
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  const colors = getColor(score);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className={`text-xs font-medium tabular-nums ${colors.text}`}>{score}</span>
      </div>
      <div className="h-1.5 bg-[var(--ed-bg-3)] rounded-full overflow-hidden">
        <div
          className={`h-full ${colors.bar} rounded-full transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function OverallScoreRing({ score }: { score: number }) {
  const colors = getColor(score);
  const radius = 26;
  const strokeWidth = 4;
  const circumference = 2 * Math.PI * radius;
  const progress = ((100 - score) / 100) * circumference;

  return (
    <div className="relative w-16 h-16">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 60 60">
        <circle
          cx="30"
          cy="30"
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-[var(--ed-bg-3)]"
        />
        <circle
          cx="30"
          cy="30"
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={progress}
          className={`${colors.bar.replace('bg-', 'stroke-')} transition-all duration-500`}
        />
      </svg>
      <div className={`absolute inset-0 flex items-center justify-center text-lg font-medium ${colors.text}`}>
        {score}
      </div>
    </div>
  );
}

export function ComparisonScoreCard({ landingPage, showIssues = true }: ComparisonScoreCardProps) {
  if (!landingPage) {
    return (
      <div className="ed-card p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <XCircle className="h-4 w-4" />
          <span className="text-sm">No landing page analysis available</span>
        </div>
        <p className="text-xs text-muted-foreground/60 mt-2">
          Run the landing page analyzer to get insights.
        </p>
      </div>
    );
  }

  const { scores, issues } = landingPage;
  const criticalCount = issues.filter(i => i.severity === 'critical').length;
  const majorCount = issues.filter(i => i.severity === 'major').length;
  const topIssues = issues.filter(i => i.severity === 'critical' || i.severity === 'major').slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Header with overall score */}
      <div className="flex items-start gap-4">
        <OverallScoreRing score={scores.overall} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">Landing Page Score</p>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{landingPage.url}</p>
          {(criticalCount > 0 || majorCount > 0) && (
            <div className="flex items-center gap-2 mt-2">
              {criticalCount > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--ed-red-bg)] text-[var(--ed-red)] border border-[rgba(224,90,82,0.12)]">
                  {criticalCount} critical
                </span>
              )}
              {majorCount > 0 && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--ed-amber-bg)] text-[var(--ed-amber)] border border-[rgba(224,168,82,0.12)]">
                  {majorCount} major
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Score breakdown */}
      <div className="space-y-2">
        <ScoreBar score={scores.messageMatch} label="Message Match" />
        <ScoreBar score={scores.visualQuality} label="Visual Quality" />
        <ScoreBar score={scores.layoutScore} label="Layout" />
        <ScoreBar score={scores.conversionOptimization} label="Conversion Opt." />
      </div>

      {/* Top issues */}
      {showIssues && topIssues.length > 0 && (
        <div className="pt-3 border-t border-border/20 space-y-2">
          <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">Key Issues</span>
          {topIssues.map((issue, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <AlertTriangle className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${
                issue.severity === 'critical' ? 'text-[var(--ed-red)]' : 'text-[var(--ed-amber)]'
              }`} />
              <span className="text-muted-foreground">{issue.title}</span>
            </div>
          ))}
        </div>
      )}

      {/* All healthy indicator */}
      {issues.length === 0 && (
        <div className="pt-3 border-t border-border/20">
          <div className="flex items-center gap-2 text-[var(--ed-green)]">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-sm font-medium">No issues detected</span>
          </div>
        </div>
      )}
    </div>
  );
}
